/**
 * CommercialEnginePanel — E16/E17/E18 admin. Mounted in the Sales command
 * center. Tabs: Engine Config (thresholds + rates), Facility Assessments,
 * Proposals (build / email / print).
 */
import { useCallback, useEffect, useState } from 'react';
import { Settings2, ClipboardList, FileText, Save } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';
const inputCls = 'w-24 rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-sm';

export default function CommercialEnginePanel() {
  const [tab, setTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [building, setBuilding] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [newType, setNewType] = useState('');

  const addServiceType = async () => {
    const name = newType.trim();
    if (name.length < 2) return;
    try {
      await apiFetch('/api/commercial-quote/service-types', { method: 'POST', body: { name } });
      setNewType(''); toast.success('Service type added'); load();
    } catch (e) { toast.error(e.detail || 'Add failed'); }
  };

  const removeServiceType = async (name) => {
    try {
      await apiFetch(`/api/commercial-quote/service-types/${encodeURIComponent(name)}`, { method: 'DELETE' });
      toast.success('Service type removed'); load();
    } catch (e) { toast.error(e.detail || 'Remove failed'); }
  };

  const load = useCallback(async () => {
    try {
      const [c, a, p, st] = await Promise.all([
        apiFetch('/api/commercial-quote/admin/config'),
        apiFetch('/api/commercial-quote/assessments'),
        apiFetch('/api/commercial-quote/proposals'),
        apiFetch('/api/commercial-quote/service-types'),
      ]);
      setConfig(c.config);
      setServiceTypes(st.types || []);
      setThresholds({
        min_account_threshold: c.config.min_account_threshold,
        min_specialty_threshold: c.config.min_specialty_threshold,
      });
      setAssessments(a.assessments || []);
      setProposals(p.proposals || []);
    } catch (_) { /* non-admin */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveThresholds = async () => {
    try {
      await apiFetch('/api/commercial-quote/admin/config', {
        method: 'PUT',
        body: {
          min_account_threshold: parseFloat(thresholds.min_account_threshold),
          min_specialty_threshold: parseFloat(thresholds.min_specialty_threshold),
        },
      });
      toast.success('Thresholds updated'); load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  const saveRates = async () => {
    try {
      await apiFetch('/api/commercial-quote/admin/config', {
        method: 'PUT', body: { industry_profiles: config.industry_profiles },
      });
      toast.success('Industry base rates updated'); load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  const buildProposal = async (a) => {
    try {
      const res = await apiFetch('/api/commercial-quote/proposals', {
        method: 'POST',
        body: {
          assessment_id: a.assessment_id,
          client_name: a.contact_name,
          facility_name: a.company_name,
          business_type: a.business_type,
          scope: `Integrated facility-care program for ${a.company_name} (${a.business_type}) — ${(a.square_footage || 0).toLocaleString()} sqft, ${a.buildings} building(s), ${a.restrooms} restrooms. ${a.special_requirements || ''}`.trim(),
          selected_package: 'professional',
          service_frequency: a.service_frequency || '5x_week',
          specialty_services: [],
          sustainability_options: [],
          estimated_low: 0, estimated_high: 0,
          contract_term_months: 12,
          account_contact: null,
          client_email: a.email,
        },
      });
      toast.success('Proposal drafted');
      setBuilding(null); load();
      window.open(`/admin/proposal/${res.proposal_id}`, '_blank');
    } catch (e) { toast.error(e.detail || 'Failed'); }
  };

  const emailProposal = async (pid) => {
    try {
      await apiFetch(`/api/commercial-quote/proposals/${pid}/email`, { method: 'POST' });
      toast.success('Proposal emailed to client'); load();
    } catch (e) { toast.error(e.detail || 'Email failed'); }
  };

  if (!config) return null;

  const TabBtn = ({ k, icon: Icon, label }) => (
    <button onClick={() => setTab(k)} data-testid={`ce-tab-${k}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${tab === k ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid="commercial-engine-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          Commercial Quote Engine · Internal
        </h2>
        <div className="flex flex-wrap gap-2 min-w-0">
          <TabBtn k="config" icon={Settings2} label="Engine Config" />
          <TabBtn k="assessments" icon={ClipboardList} label={`Assessments (${assessments.length})`} />
          <TabBtn k="proposals" icon={FileText} label={`Proposals (${proposals.length})`} />
        </div>
      </div>

      {tab === 'config' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Min account threshold ($/mo)</p>
              <input className={inputCls} type="number" value={thresholds.min_account_threshold ?? ''} onChange={(e) => setThresholds((t) => ({ ...t, min_account_threshold: e.target.value }))} data-testid="ce-min-account" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Min specialty threshold ($/mo)</p>
              <input className={inputCls} type="number" value={thresholds.min_specialty_threshold ?? ''} onChange={(e) => setThresholds((t) => ({ ...t, min_specialty_threshold: e.target.value }))} data-testid="ce-min-specialty" />
            </div>
            <button onClick={saveThresholds} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[#C9A962] text-black" data-testid="ce-save-thresholds">
              <Save className="h-3 w-3" /> Save Thresholds
            </button>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Industry base rate $/sqft/month (Essential Care baseline) — internal only</p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(config.industry_profiles || {}).map(([k, prof]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="text-xs text-zinc-400 w-20">{k}</span>
                  <input className={inputCls} type="number" step="0.005" value={prof.base_rate_sqft}
                    onChange={(e) => setConfig((c) => ({ ...c, industry_profiles: { ...c.industry_profiles, [k]: { ...c.industry_profiles[k], base_rate_sqft: parseFloat(e.target.value) || 0 } } }))}
                    data-testid={`ce-rate-${k}`} />
                </div>
              ))}
              <button onClick={saveRates} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[#C9A962] text-black" data-testid="ce-save-rates">
                <Save className="h-3 w-3" /> Save Rates
              </button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">Margin targets: Essential {config.margin_targets_pct?.essential}% · Professional {config.margin_targets_pct?.professional}% · Executive {config.margin_targets_pct?.executive}% · Specialty {config.margin_targets_pct?.specialty}%. Route density: {config.route_density_discount_pct}% transport discount within {config.route_density_radius_miles} miles. Never shown publicly.</p>
          </div>
          <div data-testid="ce-service-types">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Field Staff · Commercial Service Types (expandable)</p>
            <div className="flex flex-wrap items-center gap-2">
              {serviceTypes.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border border-[#C9A962]/60 text-[#C9A962]" data-testid={`ce-stype-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  {t}
                  <button onClick={() => removeServiceType(t)} className="text-red-400 hover:text-red-300 font-black" data-testid={`ce-stype-remove-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}>×</button>
                </span>
              ))}
              <input className="w-36 rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" placeholder="Add service type…"
                value={newType} onChange={(e) => setNewType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addServiceType(); }}
                data-testid="ce-stype-input" />
              <button onClick={addServiceType} className="px-3 py-1 rounded-full text-xs font-bold bg-[#C9A962] text-black" data-testid="ce-stype-add">Add</button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1.5">Crews pick from this list when logging commercial work in the Field Staff app.</p>
          </div>
        </div>
      )}

      {tab === 'assessments' && (
        <div className="space-y-3" data-testid="ce-assessments-list">
          {assessments.length === 0 && <p className="text-sm text-zinc-500">No facility assessments yet.</p>}
          {assessments.map((a) => (
            <div key={a.assessment_id} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`ce-assessment-${a.assessment_id}`}>
              <div>
                <p className="font-bold text-white">{a.company_name} <span className="text-zinc-500 text-xs">· {a.business_type}</span></p>
                <p className="text-xs text-zinc-400">{a.contact_name} · {a.email} · {a.phone}</p>
                <p className="text-xs mt-0.5" style={{ color: GOLD }}>{(a.square_footage || 0).toLocaleString()} sqft · {a.buildings} bldg · {a.restrooms} restrooms · {a.service_frequency || '—'}</p>
                {(a.photos || []).length > 0 && (
                  <p className="text-xs mt-1 flex flex-wrap gap-2" data-testid={`ce-assessment-photos-${a.assessment_id}`}>
                    {a.photos.map((ph, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/commercial-quote/assessments/photo?path=${encodeURIComponent(ph.path)}`}
                        target="_blank" rel="noreferrer"
                        className="underline" style={{ color: GOLD }}>
                        {ph.filename || `file ${i + 1}`}
                      </a>
                    ))}
                  </p>
                )}
              </div>
              <button onClick={() => buildProposal(a)} disabled={building === a.assessment_id}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#C9A962] text-black" data-testid={`ce-build-proposal-${a.assessment_id}`}>
                Build Proposal
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'proposals' && (
        <div className="space-y-3" data-testid="ce-proposals-list">
          {proposals.length === 0 && <p className="text-sm text-zinc-500">No proposals yet.</p>}
          {proposals.map((p) => (
            <div key={p.proposal_id} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`ce-proposal-${p.proposal_id}`}>
              <div>
                <p className="font-bold text-white">{p.facility_name} <span className="text-zinc-500 text-xs">· {p.business_type} · {p.status}</span></p>
                <p className="text-xs text-zinc-400">{p.client_name} · {p.selected_package} care · {p.contract_term_months} mo</p>
              </div>
              <div className="flex gap-2">
                <a href={`/admin/proposal/${p.proposal_id}`} target="_blank" rel="noreferrer"
                  className="px-3 py-1.5 rounded-full text-xs font-bold border border-[#C9A962] text-[#C9A962]" data-testid={`ce-view-proposal-${p.proposal_id}`}>
                  View / Print
                </a>
                {p.client_email && (
                  <button onClick={() => emailProposal(p.proposal_id)}
                    className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#C9A962] text-black" data-testid={`ce-email-proposal-${p.proposal_id}`}>
                    Email Client
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
